import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { AddressFields } from "../../components/AddressFields";
import { CurrencyInput } from "../../components/CurrencyInput";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { DirectoryDrawer } from "../../components/directory/DirectoryDrawer";
import { PhotoUploadField } from "../../components/directory/PhotoUploadField";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { artistsApi } from "../../api";
import { QueryErrorBanner, ListSkeletonCards } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { Artist } from "../../types";
import { validateAddressForm } from "../../utils/addressValidation";
import {
  ARTIST_GENRES,
  artistToForm,
  buildArtistPayload,
  emptyArtistForm,
  formatArtistContactSummary,
  formatArtistDisplayLocation,
  type ArtistFormState,
} from "../../utils/artistProfile";
import { DirectoryAvatar } from "../../components/directory/DirectoryAvatar";
import { formatINR } from "../../utils/format";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./ArtistsPage.css";

type Props = {
  workspace: WorkspaceId;
};

const ArtistsPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ArtistFormState>(emptyArtistForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Artist | null>(null);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(null);
  const [contactAttempted, setContactAttempted] = useState(false);

  const patchForm = (patch: Partial<ArtistFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.artists.list(workspace) });
  };

  const createMutation = useMutation({
    mutationFn: async (payload: ArtistFormState) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return artistsApi.create(buildArtistPayload(payload), token);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; formData: ArtistFormState }) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return artistsApi.update(payload.id, buildArtistPayload(payload.formData), token);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return artistsApi.remove(id, token);
    },
    onSuccess: invalidate,
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token || !editingId) throw new Error("Save the artist before uploading a photo.");
      return artistsApi.uploadPhoto(editingId, file, token);
    },
    onSuccess: async (updated) => {
      setUploadedPhotoUrl(updated.photo_url ?? null);
      await invalidate();
    },
  });

  const rows = useMemo<Artist[]>(() => artistsQuery.data ?? [], [artistsQuery.data]);
  const loading = artistsQuery.isPending;
  const errorMsg =
    artistsQuery.error instanceof Error ? artistsQuery.error.message : artistsQuery.error ? String(artistsQuery.error) : "";

  const resetForm = () => {
    setForm(emptyArtistForm);
    setEditingId(null);
    setFormOpen(false);
    setUploadedPhotoUrl(null);
    setContactAttempted(false);
  };

  const openCreate = () => {
    setForm(emptyArtistForm);
    setEditingId(null);
    setFormOpen(true);
    setUploadedPhotoUrl(null);
    setContactAttempted(false);
  };

  const beginEdit = (artist: Artist) => {
    setEditingId(artist.id);
    setForm(artistToForm(artist));
    setFormOpen(true);
    setUploadedPhotoUrl(artist.photo_url ?? null);
    setContactAttempted(false);
  };

  const editingArtist = editingId ? rows.find((artist) => artist.id === editingId) ?? null : null;
  const currentPhotoUrl = uploadedPhotoUrl ?? editingArtist?.photo_url ?? null;

  const hasContact = [form.email, form.phone, form.manager_phone].some((value) => value.trim());
  const contactInvalid = contactAttempted && !hasContact;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactAttempted(true);
    if (!hasContact) {
      toast.error("Add at least one way to reach this artist — email, phone or manager phone.");
      return;
    }
    const addressCheck = validateAddressForm(form);
    if (!addressCheck.ok) {
      toast.error(addressCheck.message);
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, formData: form });
        toast.success("Artist updated");
      } else {
        await createMutation.mutateAsync(form);
        toast.success("Artist created");
      }
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : editingId ? "Failed to update artist" : "Failed to create artist");
    }
  };

  const handleDelete = (artist: Artist) => {
    setPendingDelete(artist);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      if (editingId === pendingDelete.id) resetForm();
      toast.success("Artist deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete artist");
    } finally {
      setPendingDelete(null);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="app-shell-dash artists-page">
      <div className="app-shell-dash-toolbar">
        <div>
          <h1 className="app-shell-page-title artists-page__title">Artists</h1>
          <p className="app-shell-lede artists-page__lede">
            Performance profiles for onboarding, fees, and show logistics.
          </p>
        </div>
        <button type="button" className="eventos-primary-btn" onClick={openCreate}>
          Add artist
        </button>
      </div>

      {artistsQuery.isError ? (
        <QueryErrorBanner
          message={errorMsg || "Unable to load artists"}
          onRetry={() => void artistsQuery.refetch()}
          retrying={artistsQuery.isFetching}
        />
      ) : null}

      <div className="artists-page__layout directory-page__list-only">
        <div className="artists-page__list-bar">
          <span className="artists-page__count">
            {loading ? "Loading…" : `${rows.length} artist${rows.length === 1 ? "" : "s"}`}
          </span>
          <button type="button" className="eventos-nav-btn" onClick={() => void artistsQuery.refetch()} disabled={artistsQuery.isFetching}>
            {artistsQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="artists-page__list">
          {loading ? (
            <ListSkeletonCards rows={6} />
          ) : rows.length === 0 ? (
            <p className="artists-page__empty">No artists yet. Add your first artist to get started.</p>
          ) : (
            rows.map((artist) => {
              const location = formatArtistDisplayLocation(artist);
              return (
                <article
                  key={artist.id}
                  className={`artists-page__row${editingId === artist.id ? " is-editing" : ""}`}
                >
                  <DirectoryAvatar name={artist.name} photoUrl={artist.photo_url} className="artists-page__avatar" />
                  <div className="artists-page__row-main">
                    <div className="artists-page__row-head">
                      <span className="artists-page__row-name">{artist.name}</span>
                      {artist.stage_name ? <span className="artists-page__stage-name">{artist.stage_name}</span> : null}
                      {artist.genre ? <span className="artists-page__genre-pill">{artist.genre}</span> : null}
                    </div>
                    <div className="artists-page__row-details">
                      <div className="artists-page__detail">
                        <span className="artists-page__detail-label">Contact</span>
                        <span className="artists-page__detail-value artists-page__detail-value--muted">
                          {formatArtistContactSummary(artist)}
                        </span>
                      </div>
                      <div className="artists-page__detail">
                        <span className="artists-page__detail-label">Base fee</span>
                        <span className="artists-page__detail-value">
                          {artist.base_cost ? formatINR(artist.base_cost) : "—"}
                        </span>
                      </div>
                      <div className="artists-page__detail">
                        <span className="artists-page__detail-label">Base location</span>
                        <span className="artists-page__detail-value artists-page__detail-value--muted" title={location}>
                          {location}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="artists-page__row-actions">
                    <button type="button" className="eventos-nav-btn" onClick={() => beginEdit(artist)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => handleDelete(artist)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      <DirectoryDrawer
        open={formOpen}
        size="form"
        placement="center"
        title={editingId ? "Edit artist" : "Add artist"}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="eventos-form artists-page__drawer-form">
          <div className="app-form-section" role="group" aria-labelledby="artist-sec-profile">
            <div id="artist-sec-profile" className="app-form-section-title">
              Profile
            </div>
            {editingId ? (
              <PhotoUploadField
                name={form.name || "Artist"}
                photoUrl={currentPhotoUrl}
                uploading={uploadPhotoMutation.isPending}
                onUpload={(file) => uploadPhotoMutation.mutateAsync(file)}
              />
            ) : null}
            <div className="eventos-form-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Legal / billing name</span>
                <input
                  className="eventos-input"
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  placeholder="Artist or act name"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Stage name</span>
                <input
                  className="eventos-input"
                  value={form.stage_name}
                  onChange={(e) => patchForm({ stage_name: e.target.value })}
                  placeholder="As billed on posters (optional)"
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Genre / act type</span>
                <MobileSelect
                  label="Genre"
                  className="eventos-select"
                  value={form.genre}
                  onChange={(v) => patchForm({ genre: v })}
                  options={[
                    { value: "", label: "Select genre (optional)" },
                    ...ARTIST_GENRES.map((genre) => ({ value: genre, label: genre })),
                  ]}
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Base fee</span>
                <CurrencyInput
                  value={form.base_cost}
                  onChange={(e) => patchForm({ base_cost: e.target.value })}
                  placeholder="Standard performance fee"
                />
              </label>
            </div>
          </div>

          <div className="app-form-section" role="group" aria-labelledby="artist-sec-contact">
            <div id="artist-sec-contact" className="app-form-section-title">
              Contact
            </div>
            <p id="artist-contact-hint" className="app-form-section-lede">
              At least one of email, phone or manager phone is required.
            </p>
            <div className="eventos-form-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Email</span>
                <input
                  className="eventos-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => patchForm({ email: e.target.value })}
                  placeholder="artist@agency.com"
                  aria-describedby="artist-contact-hint"
                  aria-invalid={contactInvalid || undefined}
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Phone</span>
                <input
                  className="eventos-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => patchForm({ phone: e.target.value })}
                  placeholder="Phone number"
                  aria-describedby="artist-contact-hint"
                  aria-invalid={contactInvalid || undefined}
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Manager / agent</span>
                <input
                  className="eventos-input"
                  value={form.manager_name}
                  onChange={(e) => patchForm({ manager_name: e.target.value })}
                  placeholder="Name (optional)"
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Manager phone</span>
                <input
                  className="eventos-input"
                  type="tel"
                  value={form.manager_phone}
                  onChange={(e) => patchForm({ manager_phone: e.target.value })}
                  placeholder="Manager phone"
                  aria-describedby="artist-contact-hint"
                  aria-invalid={contactInvalid || undefined}
                />
              </label>
            </div>
          </div>

          <div className="app-form-section" role="group" aria-labelledby="artist-sec-base">
            <div id="artist-sec-base" className="app-form-section-title">
              Base location
            </div>
            <p className="app-form-section-lede">Where the artist or crew is ordinarily based (full postal address).</p>
            <AddressFields
              form={form}
              onChange={patchForm}
              idPrefix={editingId ?? "new-artist"}
              variant="artist"
              artists={rows}
              districtOrphanValue={editingId ? form.district : null}
            />
          </div>

          <div className="app-form-section" role="group" aria-labelledby="artist-sec-notes">
            <div id="artist-sec-notes" className="app-form-section-title">
              Notes
            </div>
            <label className="eventos-field artists-page__notes-field">
              <span className="eventos-field-label">Internal notes</span>
              <textarea
                className="eventos-input"
                rows={3}
                value={form.notes}
                onChange={(e) => patchForm({ notes: e.target.value })}
                placeholder="Rider highlights, travel restrictions, billing notes…"
              />
            </label>
          </div>

          <div className="app-form-actions">
            <button className="eventos-primary-btn" type="submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save changes" : "Create artist"}
            </button>
            <button type="button" className="eventos-nav-btn" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </DirectoryDrawer>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete artist?"
        message={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed from your directory. Any events or schedules referencing this artist will no longer show them (those links are cleared, not deleted).`
            : ""
        }
        confirmLabel="Delete"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ArtistsPage;
